// Small Leaflet wrapper: draw a GeoJSON polygon on a neutral basemap, with
// a Map / Satellite toggle. CARTO Positron for the flat view, Esri World
// Imagery for the aerial (both free, no API key required).

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { polygonBoundsLatLng } from '../../shared/geometry.js';

const CARTO_POSITRON = {
  url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}{r}.png',
  options: {
    subdomains: 'abcd',
    maxZoom: 19,
    attribution: '© OpenStreetMap · © CARTO',
  },
};

const ESRI_WORLD_IMAGERY = {
  url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  options: {
    maxZoom: 19,
    attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
  },
};

export function ParcelMap({ geojson, neighbors = [], height = 320 }) {
  const container = document.createElement('div');
  container.style.position = 'relative';
  container.style.width = '100%';
  container.style.height = `${height}px`;

  const mapEl = document.createElement('div');
  mapEl.style.height = '100%';
  mapEl.style.width = '100%';
  mapEl.style.border = '1px solid var(--color-rule)';
  mapEl.style.borderRadius = '2px';
  mapEl.style.background = 'var(--color-paper-deep)';
  container.appendChild(mapEl);

  if (!geojson?.coordinates?.[0]?.length) {
    mapEl.style.display = 'grid';
    mapEl.style.placeItems = 'center';
    mapEl.style.color = 'var(--color-ink-faint)';
    mapEl.textContent = 'No polygon available';
    return container;
  }

  queueMicrotask(() => {
    const bounds = polygonBoundsLatLng(geojson);
    const map = L.map(mapEl, {
      zoomControl: true,
      scrollWheelZoom: false,
      attributionControl: true,
    });

    const flatLayer = L.tileLayer(CARTO_POSITRON.url, CARTO_POSITRON.options);
    const aerialLayer = L.tileLayer(ESRI_WORLD_IMAGERY.url, ESRI_WORLD_IMAGERY.options);

    // Label overlay when on aerial — street names + labels stay readable.
    const labelsOverlay = L.tileLayer(
      'https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_only_labels/{z}/{x}/{y}{r}.png',
      { subdomains: 'abcd', maxZoom: 19, opacity: 0.9, pane: 'shadowPane' },
    );

    let currentBase = 'flat';
    flatLayer.addTo(map);

    // Neighbouring parcels first (underneath the main parcel).
    const neighborGeoJSON = {
      type: 'FeatureCollection',
      features: (neighbors ?? [])
        .filter((n) => n.polygon_wgs84)
        .map((n) => ({ type: 'Feature', geometry: n.polygon_wgs84, properties: {} })),
    };
    const neighborLayer = L.geoJSON(neighborGeoJSON, {
      style: () => neighborStyle(currentBase),
    }).addTo(map);

    // Main parcel polygon — stroke in ink when on aerial, olive on flat.
    const polygon = L.geoJSON(geojson, {
      style: () => polygonStyle(currentBase),
    }).addTo(map);

    if (bounds) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 19 });

    // Toggle control — top right
    const toggle = document.createElement('div');
    toggle.className = 'parcel-map-toggle';
    Object.assign(toggle.style, {
      position: 'absolute', top: '10px', right: '10px', zIndex: '1000',
      display: 'inline-flex',
      background: 'var(--color-paper)',
      border: '1px solid var(--color-rule)',
      borderRadius: '2px',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
      font: '500 0.72rem/1 var(--font-sans)',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    });
    const btn = (label, key) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.dataset.view = key;
      Object.assign(b.style, {
        padding: '0.45rem 0.8rem', border: '0', background: 'transparent',
        color: 'var(--color-ink-soft)', cursor: 'pointer',
      });
      b.onclick = () => setView(key);
      return b;
    };
    const mapBtn = btn('Map', 'flat');
    const satBtn = btn('Satellite', 'aerial');
    toggle.appendChild(mapBtn);
    toggle.appendChild(satBtn);
    container.appendChild(toggle);

    function setView(key) {
      if (key === currentBase) return;
      if (key === 'aerial') {
        map.removeLayer(flatLayer);
        aerialLayer.addTo(map);
        labelsOverlay.addTo(map);
      } else {
        map.removeLayer(aerialLayer);
        map.removeLayer(labelsOverlay);
        flatLayer.addTo(map);
      }
      currentBase = key;
      polygon.setStyle(polygonStyle(key));
      neighborLayer.setStyle(neighborStyle(key));
      updateActive();
    }
    function updateActive() {
      for (const b of [mapBtn, satBtn]) {
        const active = b.dataset.view === currentBase;
        b.style.background = active ? 'var(--color-ink)' : 'transparent';
        b.style.color = active ? 'var(--color-paper)' : 'var(--color-ink-soft)';
      }
    }
    updateActive();

    const ro = new ResizeObserver(() => {
      map.invalidateSize();
      if (bounds) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 19 });
    });
    ro.observe(mapEl);
  });

  return container;
}

function neighborStyle(view) {
  if (view === 'aerial') {
    return {
      color: '#ffffff', weight: 1, opacity: 0.7, fillOpacity: 0,
      dashArray: '3,3', lineJoin: 'miter',
    };
  }
  return {
    color: 'oklch(65% 0.010 55)', weight: 1, opacity: 0.7, fillOpacity: 0,
    dashArray: '3,3', lineJoin: 'miter',
  };
}

function polygonStyle(view) {
  if (view === 'aerial') {
    return {
      color: '#ffffff', weight: 2.5, opacity: 0.95,
      fillColor: '#ffffff', fillOpacity: 0.12,
      lineCap: 'square', lineJoin: 'miter',
    };
  }
  return {
    color: '#46613d', weight: 2, opacity: 0.95,
    fillColor: '#5a7a4e', fillOpacity: 0.22,
    lineCap: 'square', lineJoin: 'miter',
  };
}
